import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#050505] text-[#D4D4D8] pb-24">
      <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <Link to="/" className="text-white" data-testid="back-to-home">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="font-bold text-white font-['Unbounded'] text-sm">Terms of Service</h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 text-sm leading-relaxed space-y-5">
        <p className="text-xs text-[#71717A]">Last updated: 14 February 2026</p>

        <Section title="Agreement">
          <p>
            By creating an account on Animus you agree to these Terms of Service. If you do not agree, do not
            use the service.
          </p>
        </Section>

        <Section title="Age (18+)">
          <p>
            Animus is for adults <b>18 years or older</b>. The service contains explicit and adult content.
            Account holders must be of legal age in their jurisdiction. If we discover that an account belongs
            to a minor, we will delete it immediately.
          </p>
        </Section>

        <Section title="Acceptable use">
          <ul className="list-disc pl-5 space-y-1">
            <li>You may roleplay any fictional adult scenario with AI characters.</li>
            <li>You may <b>not</b> generate, request, or upload content involving minors (real or fictional).</li>
            <li>You may not use the service for harassment, threats, doxxing, or hate speech.</li>
            <li>You may not attempt to break, scrape, or reverse-engineer the service.</li>
            <li>You may not impersonate real people without consent.</li>
            <li>You may not upload copyrighted material you don't own.</li>
            <li>Violations result in account termination without refund.</li>
          </ul>
        </Section>

        <Section title="Content you create">
          <p>
            You retain ownership of the characters and personas you create. By uploading content you grant
            Animus a non-exclusive licence to host, display, and process it to provide the service. You are
            responsible for the content you upload.
          </p>
        </Section>

        <Section title="AI-generated content">
          <p>
            AI replies are generated by third-party models and may be inaccurate, offensive, or inconsistent.
            They are fiction. We do not endorse, vouch for, or take responsibility for what the AI produces.
            Do not treat AI replies as factual advice (legal, medical, financial, etc.).
          </p>
        </Section>

        <Section title="Subscriptions, gems & boosts">
          <ul className="list-disc pl-5 space-y-1">
            <li>Premium and Ultimate are recurring monthly subscriptions billed via Stripe.</li>
            <li>You can cancel anytime in Settings. Cancellation takes effect at the end of the billing period.</li>
            <li>Gem purchases and Boost purchases are one-time and <b>non-refundable</b> except where required by law.</li>
            <li>Prices are in GBP and may change with 30 days notice.</li>
          </ul>
        </Section>

        <Section title="Refunds">
          <p>
            Subscription payments are non-refundable except where required by UK consumer law. If you believe
            a payment was charged in error, contact <a href="mailto:support@animusapp.ai" className="text-[#E11D48]">support@animusapp.ai</a> within 14 days.
          </p>
        </Section>

        <Section title="Account termination">
          <p>
            You can delete your account anytime in Settings → Account. We may suspend or terminate accounts
            that violate these Terms, without refund, at our sole discretion.
          </p>
        </Section>

        <Section title="Disclaimers">
          <p>
            Animus is provided "as is" without warranties of any kind. We do not guarantee uptime, accuracy,
            or fitness for any particular purpose. We are not liable for indirect or consequential damages
            beyond the amount you have paid us in the last 12 months.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            We may update these Terms. Material changes will be notified by in-app banner or email. Continued
            use after changes means you accept them.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These Terms are governed by the laws of England and Wales.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions? Email <a href="mailto:support@animusapp.ai" className="text-[#E11D48]">support@animusapp.ai</a>.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-1.5">
      <h2 className="text-white font-['Unbounded'] text-base">{title}</h2>
      <div className="text-[#A1A1AA] text-sm">{children}</div>
    </section>
  );
}
